import { CrudViewColumnType, CrudViewFormItemType } from '@rappider/rappider-components/utils';
import { Validators } from '@angular/forms';
import { EntityConfigMetadata, EntityField } from '../models/entity-config.models'; // Adjust the import path as needed

/**
 * Metadata configuration for the AiFunctionCall entity.
 * This object is used by an EntityService to dynamically generate
 * a full CRUD interface (list, create, and update views).
 */
export const AI_FUNCTION_CALL_ENTITY_META_CONFIG: EntityConfigMetadata = {
  // --- Basic Entity Information ---
  name: 'AiFunctionCall',
  title: 'AI Functions',
  description: 'Manage AI-powered functions that can be called to perform specific tasks or generate structured data.',
  apiPath: 'ai-function-calls',
  dataFetchedFrom: 'loopback-api', // Assuming the same data source type
  titleFieldName: 'name',
  entityDataCreateTransformFunction: (data: any) => {
    const transformed = { ...data };

    // Map eventId to postWorkflowEventId (API expects postWorkflowEventId)
    if (transformed.eventId) {
      transformed.postWorkflowEventId = transformed.eventId;
      delete transformed.eventId;
    }

    // Ensure responseJsonSample is an object (required field)
    if (!transformed.responseJsonSample || typeof transformed.responseJsonSample === 'string') {
      transformed.responseJsonSample = transformed.responseJsonSample && transformed.responseJsonSample.trim()
        ? JSON.parse(transformed.responseJsonSample)
        : {};
    }

    // Ensure exposeAsFeature is boolean
    transformed.exposeAsFeature = Boolean(transformed.exposeAsFeature);

    // Convert index to number or remove if invalid
    if (transformed.index !== null && transformed.index !== undefined && transformed.index !== '') {
      const numValue = Number(transformed.index);
      transformed.index = isNaN(numValue) ? undefined : numValue;
    }
    if (transformed.index === undefined || transformed.index === null) {
      delete transformed.index;
    }

    // Remove empty optional fields
    ['responseJsonSchema', 'postWorkflowStepFunctionId', 'id', 'postWorkflowEventId'].forEach(field => {
      if (!transformed[field]) {
        delete transformed[field];
      }
    });

    // Validate temperature
    if (typeof transformed.temperature !== 'number' || transformed.temperature < 0 || transformed.temperature > 1) {
      transformed.temperature = 0.2;
    }

    return transformed;
  },
  entityDataUpdateTransformFunction: (data: any) => {
    const transformed = { ...data };

    // Map eventId to postWorkflowEventId
    if (transformed.eventId) {
      transformed.postWorkflowEventId = transformed.eventId;
      delete transformed.eventId;
    }

    // Ensure responseJsonSample is an object
    if (!transformed.responseJsonSample || typeof transformed.responseJsonSample === 'string') {
      transformed.responseJsonSample = transformed.responseJsonSample && transformed.responseJsonSample.trim()
        ? JSON.parse(transformed.responseJsonSample)
        : {};
    }

    // Ensure exposeAsFeature is boolean
    transformed.exposeAsFeature = Boolean(transformed.exposeAsFeature);

    // Convert index to number or remove if invalid
    if (transformed.index !== null && transformed.index !== undefined && transformed.index !== '') {
      const numValue = Number(transformed.index);
      transformed.index = isNaN(numValue) ? undefined : numValue;
    }
    if (transformed.index === undefined || transformed.index === null) {
      delete transformed.index;
    }

    // Remove empty optional fields
    ['responseJsonSchema', 'postWorkflowStepFunctionId', 'id', 'postWorkflowEventId'].forEach(field => {
      if (!transformed[field]) {
        delete transformed[field];
      }
    });

    // Validate temperature
    if (typeof transformed.temperature !== 'number' || transformed.temperature < 0 || transformed.temperature > 1) {
      transformed.temperature = 0.2;
    }

    return transformed;
  },
  // --- Field Definitions ---
  fields: <EntityField[]>[
    {
      fieldName: 'name',
      title: 'Name',
      dataType: 'string',
      displayFieldType: CrudViewColumnType.Link,
      formFieldType: CrudViewFormItemType.TextBox,
      isRequired: true,
      formValidators: [
        {
          type: Validators.required,
          errorKey: 'required',
          errorMessage: 'Name is required.'
        },
        {
          type: Validators.pattern('^[a-zA-Z0-9_]+$'),
          errorKey: 'pattern',
          errorMessage: 'Name must be alphanumeric and can include underscores.'
        }
      ],
      inputDescription: 'The name of the function, used for identification in code. Do not use spaces or special characters.',
      hint: 'A unique programmatic name for the function, e.g., "getUserDetails".'
    },
    {
      fieldName: 'title',
      title: 'Title',
      dataType: 'string',
      displayFieldType: CrudViewColumnType.Text,
      formFieldType: CrudViewFormItemType.TextBox,
      isRequired: true,
      formValidators: [{
        type: Validators.required,
        errorKey: 'required',
        errorMessage: 'This field is required.'
      }],
      hint: 'A human-readable title for the function.'
    },
    {
      fieldName: 'description',
      title: 'Description',
      dataType: 'string',
      displayFieldType: CrudViewColumnType.Text,
      formFieldType: CrudViewFormItemType.TextArea,
      isRequired: true,
      formValidators: [{
        type: Validators.required,
        errorKey: 'required',
        errorMessage: 'This field is required.'
      }],
      hint: 'A detailed description of what this function does.'
    },
    {
      fieldName: 'exposeAsFeature',
      title: 'Expose As Feature',
      dataType: 'boolean',
      displayFieldType: CrudViewColumnType.Boolean,
      formFieldType: CrudViewFormItemType.CheckBox,
      defaultValue: false,
      isRequired: false
    },
    {
      fieldName: 'systemPrompt',
      title: 'System Prompt',
      dataType: 'string',
      displayFieldType: CrudViewColumnType.Text,
      formFieldType: CrudViewFormItemType.TextArea,
      isRequired: true,
      formValidators: [{
        type: Validators.required,
        errorKey: 'required',
        errorMessage: 'This field is required.'
      }],
      minRows: 4,
      autosize: true,
      maxRows: 16,
      hint: 'The system-level instructions for the AI model.'
    },
    {
      fieldName: 'functionPrompt',
      title: 'Function Prompt',
      dataType: 'string',
      displayFieldType: CrudViewColumnType.Text,
      formFieldType: CrudViewFormItemType.TextArea,
      isRequired: true,
      formValidators: [{
        type: Validators.required,
        errorKey: 'required',
        errorMessage: 'This field is required.'
      }],
      hint: 'The specific prompt for executing this function.'
    },
    {
      fieldName: 'responseJsonSample',
      title: 'Response JSON Sample',
      dataType: 'object',
      displayFieldType: CrudViewColumnType.Code,
      formFieldType: CrudViewFormItemType.MonacoCodeEditor,
      isRequired: false,
      defaultValue: {},
      hint: 'An example of the expected JSON output.'
    },
    {
      fieldName: 'responseJsonSchema',
      title: 'Response JSON Schema',
      dataType: 'object',
      displayFieldType: CrudViewColumnType.Code,
      formFieldType: CrudViewFormItemType.MonacoCodeEditor,
      isRequired: false,
      hint: 'The JSON schema that the AI model must adhere to for its response.',
      inputDescription: 'This schema defines the structure of the expected response from the AI model. If you provided a "Response JSON Sample", Rapider will attempt to generate this schema automatically.'
    },
    {
      fieldName: 'temperature',
      title: 'Temperature',
      dataType: 'number',
      displayFieldType: CrudViewColumnType.Number,
      formFieldType: CrudViewFormItemType.Number,
      isRequired: true,
      formValidators: [{
        type: Validators.required,
        errorKey: 'required',
        errorMessage: 'This field is required.'
      }],
      hint: 'Controls randomness. Lower is more deterministic (e.g., 0.2).'
    },
    {
      fieldName: 'index',
      title: 'Index',
      dataType: 'number',
      displayFieldType: CrudViewColumnType.Number,
      formFieldType: CrudViewFormItemType.Number,
      isRequired: false,
      hint: 'The order or priority of the function.'
    },
    {
      fieldName: 'postWorkflowStepFunctionId',
      title: 'Post Workflow Step Function ID',
      dataType: 'string',
      displayFieldType: CrudViewColumnType.Text,
      formFieldType: CrudViewFormItemType.TextBox,
      isRequired: false,
      hint: 'A Workflow Step Function to run after this one completes.'
    },
    {
      fieldName: 'postWorkflowEventId',
      title: 'Post Workflow Event ID',
      dataType: 'string',
      displayFieldType: CrudViewColumnType.Text,
      formFieldType: CrudViewFormItemType.TextBox,
      isRequired: false,
      hint: 'The ID of the event to trigger after this function completes.'
    }
  ],

};
